import { ReactNode } from "react";
import { Dialog, IconButton, Grow } from "@mui/material";
import { useTranslation } from "next-i18next";
import { IconClose } from "src/components/icons-mui/close";

/**
 * Themed Dialog component following wikifolio UI Library design specifications
 *
 * https://www.figma.com/design/zzvO7fdaTfLvgTSrb6ypCs/wikifolio-UI-Library?node-id=3431-2441&m=dev
 *
 */

export const WF_DIALOG_SPACE_VARIANTS = {
    DEFAULT: "default",
    MOBILE_SPACIOUS_TOP: "mobile-spacious-top",
} as const;

export type WfDialogSpaceVariant = (typeof WF_DIALOG_SPACE_VARIANTS)[keyof typeof WF_DIALOG_SPACE_VARIANTS];

const getSpacing = (space: WfDialogSpaceVariant) => {
    return space === WF_DIALOG_SPACE_VARIANTS.MOBILE_SPACIOUS_TOP
        ? {
              pt: { xs: 9, sm: 5 },
              pb: { xs: 3, sm: 5 },
              px: { xs: 3, sm: 5 },
          }
        : {
              p: { xs: 3, sm: 5 },
          };
};

interface IWfDialogProps {
    isOpen: boolean;
    onClose: () => void;
    children: ReactNode;
    ariaLabelledBy?: string;
    ariaDescribedBy?: string;
    showCloseButton?: boolean;
    space?: WfDialogSpaceVariant;
}

export const WfDialog = ({
    isOpen,
    onClose,
    children,
    ariaLabelledBy,
    ariaDescribedBy,
    showCloseButton = true,
    space = WF_DIALOG_SPACE_VARIANTS.DEFAULT,
}: IWfDialogProps) => {
    const { t } = useTranslation("common");

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            fullWidth
            slots={{ transition: Grow }}
            slotProps={{
                transition: {
                    timeout: 250,
                    style: { transformOrigin: "center" },
                },
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby={ariaLabelledBy}
            aria-describedby={ariaDescribedBy}
            sx={{
                "& .MuiDialog-paper": {
                    width: { xs: "100%", sm: "750px", md: "750px", lg: "708px" },
                    borderRadius: { xs: 0, sm: 1 },
                    m: 0,
                    minHeight: { xs: "100%", sm: "auto" },
                    position: "relative",
                    ...getSpacing(space),
                },
            }}
        >
            {showCloseButton && (
                <IconButton
                    variant="ghost"
                    sx={{
                        position: "absolute",
                        top: { xs: "24px", sm: "40px" },
                        right: { xs: "24px", sm: "40px" },
                        borderRadius: "6px",
                        zIndex: 1,
                    }}
                    size="small"
                    aria-label={t("close")}
                    onClick={onClose}
                >
                    <IconClose sx={{ width: "24px", height: "24px" }} />
                </IconButton>
            )}
            {children}
        </Dialog>
    );
};
